import React from 'react';

/**
 * MarkdownLegal · pinta un documento legal escrito en Markdown.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *   MEDIDO EN PRODUCCIÓN el 26-09-2026, en canal.metodopilardominguez.com:
 *   la Política de Privacidad y las Condiciones Generales se veían EN CRUDO,
 *   con los símbolos a la vista:
 *
 *       # CLÁUSULA INFORMATIVA DE PROTECCIÓN DE DATOS
 *       | Campo | Información |
 *       |-------|-------------|
 *       | **Responsable** | Centro Pilar Domínguez SL |
 *
 *   La tabla de la Privacidad —quién es el responsable, para qué se usan sus
 *   datos y cuáles son sus derechos— salía como una fila de palotes. Y es
 *   justo lo que la clienta tiene que leer ANTES de firmar.
 *
 *   Ann: «sí» a arreglarlo. No se toca ni una palabra del documento.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 🔒 POR QUÉ NO USA `dangerouslySetInnerHTML`
 *   Porque no hace falta. Devuelve elementos de React, así que **es imposible
 *   inyectar HTML** por el contenido del documento. Estos textos los escriben
 *   administradores, pero un documento legal es exactamente donde NO conviene
 *   confiar en que nadie pegue nunca algo raro.
 *
 * 🔒 Y POR QUÉ NO SE AÑADE UNA LIBRERÍA
 *   El proyecto no tiene ninguna de Markdown. Meter una dependencia nueva para
 *   pintar dos páginas obliga a tocar el `package.json` y el despliegue. Esto
 *   son 150 líneas, sin dependencias, y cubre lo que estos documentos usan:
 *   títulos, negritas, tablas, citas, listas y separadores.
 *
 * ⚠️ NO es un Markdown completo, y no pretende serlo. Lo que no entiende lo
 *   enseña tal cual, que es lo que hacía antes: nunca se pierde texto.
 */

// ── Negritas y enlaces dentro de una línea ──────────────────────────────────
// Solo se aceptan enlaces http, https y mailto. Un `javascript:` en un
// documento legal no se pinta como enlace: se queda como texto.
const ENLACE_SEGURO = /^(https?:|mailto:)/i;

function trozos(texto, clave) {
  const salida = [];
  // **negrita** · [texto](url)
  const re = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;
  let ultimo = 0;
  let m;
  let i = 0;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) salida.push(texto.slice(ultimo, m.index));
    const bloque = m[0];
    if (bloque.startsWith('**')) {
      salida.push(<strong key={`${clave}-b${i}`}>{bloque.slice(2, -2)}</strong>);
    } else {
      const corte = bloque.indexOf('](');
      const etiqueta = bloque.slice(1, corte);
      const url = bloque.slice(corte + 2, -1).trim();
      salida.push(ENLACE_SEGURO.test(url)
        ? <a key={`${clave}-a${i}`} href={url} target="_blank" rel="noreferrer">{etiqueta}</a>
        : bloque);
    }
    ultimo = m.index + bloque.length;
    i += 1;
  }
  if (ultimo < texto.length) salida.push(texto.slice(ultimo));
  return salida.length ? salida : texto;
}

// ── Una fila de tabla: | a | b | → ['a', 'b'] ───────────────────────────────
const esFila = (l) => l.trim().startsWith('|') && l.trim().endsWith('|');
const esSeparador = (l) => /^\|[\s:|-]+\|$/.test(l.trim());
const celdas = (l) => l.trim().slice(1, -1).split('|').map((c) => c.trim());

export default function MarkdownLegal({ texto }) {
  if (!texto) return null;
  const lineas = String(texto).split('\n');
  const fuera = [];
  let parrafo = [];
  let lista = null;

  const cerrarParrafo = () => {
    if (!parrafo.length) return;
    const k = `p${fuera.length}`;
    fuera.push(<p key={k} className="mdl-p">{trozos(parrafo.join(' '), k)}</p>);
    parrafo = [];
  };
  const cerrarLista = () => {
    if (!lista) return;
    const k = `l${fuera.length}`;
    fuera.push(
      <ul key={k} className="mdl-ul">
        {lista.map((t, j) => <li key={`${k}-${j}`}>{trozos(t, `${k}-${j}`)}</li>)}
      </ul>,
    );
    lista = null;
  };
  const cerrarTodo = () => { cerrarParrafo(); cerrarLista(); };

  for (let i = 0; i < lineas.length; i += 1) {
    const linea = lineas[i];
    const limpia = linea.trim();

    if (!limpia) { cerrarTodo(); continue; }

    // Tabla: se junta entera de una vez.
    if (esFila(limpia) && i + 1 < lineas.length && esSeparador(lineas[i + 1])) {
      cerrarTodo();
      const cabecera = celdas(limpia);
      const filas = [];
      let j = i + 2;
      while (j < lineas.length && esFila(lineas[j])) { filas.push(celdas(lineas[j])); j += 1; }
      const k = `t${fuera.length}`;
      fuera.push(
        <div key={k} className="mdl-tabla-caja">
          <table className="mdl-tabla">
            <thead><tr>{cabecera.map((c, n) => <th key={`${k}-h${n}`}>{trozos(c, `${k}-h${n}`)}</th>)}</tr></thead>
            <tbody>
              {filas.map((f, n) => (
                <tr key={`${k}-r${n}`}>{f.map((c, m2) => <td key={`${k}-r${n}c${m2}`}>{trozos(c, `${k}-r${n}c${m2}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      i = j - 1;
      continue;
    }

    // Títulos
    const th = limpia.match(/^(#{1,6})\s+(.*)$/);
    if (th) {
      cerrarTodo();
      const nivel = Math.min(th[1].length, 6);
      const Etiqueta = `h${nivel}`;
      const k = `h${fuera.length}`;
      fuera.push(<Etiqueta key={k} className={`mdl-h mdl-h${nivel}`}>{trozos(th[2], k)}</Etiqueta>);
      continue;
    }

    // Separador
    if (/^([-*_])\1{2,}$/.test(limpia)) { cerrarTodo(); fuera.push(<hr key={`hr${fuera.length}`} className="mdl-hr" />); continue; }

    // Cita
    if (limpia.startsWith('>')) {
      cerrarTodo();
      const k = `q${fuera.length}`;
      fuera.push(<blockquote key={k} className="mdl-cita">{trozos(limpia.replace(/^>\s?/, ''), k)}</blockquote>);
      continue;
    }

    // Lista (· - * y también 1. 2. 3.)
    const li = limpia.match(/^(?:[-*·]\s+|\d+[.)]\s+)(.*)$/);
    if (li) { cerrarParrafo(); (lista = lista || []).push(li[1]); continue; }

    cerrarLista();
    parrafo.push(limpia);
  }
  cerrarTodo();

  return (
    <div className="mdl">
      <style>{ESTILOS}</style>
      {fuera}
    </div>
  );
}

// Los estilos viajan con el componente a propósito: la clase `onb-normativa`
// que había no estaba definida en ninguna hoja del proyecto (comprobado), así
// que no pintaba nada. Aquí no se puede olvidar nadie de incluirlos.
const ESTILOS = `
.mdl { color: #12332e; font-size: 15.5px; line-height: 1.65; }
.mdl .mdl-h { color: #0d2b27; line-height: 1.3; margin: 26px 0 10px; }
.mdl .mdl-h1 { font-size: 23px; font-weight: 700; margin-top: 0; }
.mdl .mdl-h2 { font-size: 19px; font-weight: 700; }
.mdl .mdl-h3 { font-size: 16.5px; font-weight: 700; }
.mdl .mdl-h4, .mdl .mdl-h5, .mdl .mdl-h6 { font-size: 15.5px; font-weight: 700; }
.mdl .mdl-p { margin: 0 0 13px; }
.mdl .mdl-ul { margin: 0 0 13px; padding-left: 22px; }
.mdl .mdl-ul li { margin-bottom: 6px; }
.mdl .mdl-hr { border: 0; border-top: 1px solid #dfe8e6; margin: 22px 0; }
.mdl .mdl-cita {
  margin: 0 0 13px; padding: 10px 14px;
  border-left: 3px solid #077069; background: #f2f8f7; border-radius: 0 6px 6px 0;
}
.mdl a { color: #077069; font-weight: 600; }
/* La tabla es lo que peor se veía: sale con su caja, y en el móvil se puede
   arrastrar de lado en vez de desbordarse fuera de la pantalla. */
.mdl .mdl-tabla-caja { overflow-x: auto; margin: 0 0 16px; -webkit-overflow-scrolling: touch; }
.mdl .mdl-tabla { width: 100%; border-collapse: collapse; font-size: 14.5px; min-width: 380px; }
.mdl .mdl-tabla th, .mdl .mdl-tabla td {
  border: 1px solid #dfe8e6; padding: 9px 11px; text-align: left; vertical-align: top;
}
.mdl .mdl-tabla th { background: #f2f8f7; font-weight: 700; color: #0d2b27; }
.mdl .mdl-tabla tr:nth-child(even) td { background: #fafcfc; }
`;
